import { useEffect, useState } from "react";
import { format, getRoiList } from "../utils";
import { aiConfigService } from "../services/api/aiConfigService";
import axiosClient from "../services/axiosClient";
import axiosClient_v2 from "../services/axiosClient_v2";
import { AI_SERVICE_CODES, AI_CONFIG_CODES, NOTIFICATION_CODES } from "../constant/index";
import { hasActiveAiService, parseDetailToJson, calculatePositionCoordinates } from "../utils/helper";

/**
 * Custom hook to manage car park monitoring features
 * @param {Object} item - Camera item
 * @param {Array} nfc - Notification array
 * @param {Function} getCountCarPark - Callback to update parent with car park count
 * @param {Object} refWrapperVideo - Reference to video wrapper element
 * @param {number} refreshCamera - Refresh trigger
 * @returns {Object} - Car park monitoring state and functions
 */
export const useCarParkMonitoring = (
    item,
    nfc,
    getCountCarPark,
    refWrapperVideo,
    refreshCamera
) => {
    const [countPositionCarPart, setCountPositionCarPart] = useState({
        show: false,
        count: 0,
        total: 0,
    });
    const [showModalPositionCarPark, setShowModalPositionCarPark] = useState(false);
    const [listPosition, setListPosition] = useState();

    // Handle car park notification events
    useEffect(() => {
        const event = nfc?.find((n) => n?.code === item?.code);

        if (event?.type_notification_code === NOTIFICATION_CODES.CAR_PARK && event?.object_detail) {
            const carPark = {
                show: true,
                count: event?.object_detail?.use_slot || 0,
                total: event?.object_detail?.total_slot || 0,
                ...event,
            };
            setCountPositionCarPart(carPark);
            getCountCarPark(carPark);
        }
    }, [nfc, item?.code, getCountCarPark]);

    // Get list of parameters for car park
    const getListParams = async (code, cameraCode) => {
        try {
            const param = {
                codeService: code,
                cameraCode: cameraCode,
            };
            const res = await aiConfigService.get(param);
            setShowModalPositionCarPark(res[0]?.CODE === AI_CONFIG_CODES.SHOW_PARKING_POSITION);
        } catch (error) {
            throw error;
        }
    };

    // Get current empty slots count
    const getNewEmptySlot = async (code, cameraCode) => {
        try {
            const date = new Date();
            const day = {
                date: format(date),
            };
            const res = await axiosClient_v2.get("/camAI/get-list-cam-ai/", {
                params: {
                    ai_service_code: code,
                    camera_code: cameraCode,
                    ...day,
                    page: 1,
                    size: 1,
                },
            });
            const detail = parseDetailToJson(res?.data[0]?.detail);
            setCountPositionCarPart((pre) => ({
                ...pre,
                count: detail?.use_slot || 0,
            }));
        } catch (error) {
            throw error;
        }
    };

    // Get parking positions from ROI list
    const getObjectCameraUniALPR = async (cameraCode) => {
        const width = refWrapperVideo?.current?.clientWidth;
        const height = refWrapperVideo?.current?.clientHeight;

        if (!width || !height) return;

        try {
            const res = await axiosClient.get(
                `aiConfigRoiList/get-list-ai-config-roi-list/?camera_code=${cameraCode}&ai_service_code=${AI_SERVICE_CODES.CAR_PARK_MONITORING}`
            );

            if (res && res.length > 0) {
                let data = {};
                res.forEach((item) => {
                    data = item;
                });

                const arr = getRoiList(data?.ROI_LIST);
                const position = calculatePositionCoordinates(arr, width, height);

                if (position) {
                    setListPosition(position);
                    setCountPositionCarPart((pre) => ({
                        ...pre,
                        total: position?.length || 0,
                    }));
                }
            }
        } catch (error) {
            console.log(error);
        }
    };

    // Load parking positions when video wrapper is ready
    useEffect(() => {
        if (refWrapperVideo?.current) {
            getObjectCameraUniALPR(item?.code);
        }
    }, [
        item?.code,
        refWrapperVideo?.current?.clientWidth,
        refWrapperVideo?.current?.clientHeight,
        refreshCamera,
    ]);

    // Check if car park monitoring is enabled and load data
    useEffect(() => {
        const check = hasActiveAiService(item, AI_SERVICE_CODES.CAR_PARK_MONITORING);

        if (check) {
            getListParams(AI_SERVICE_CODES.CAR_PARK_MONITORING, item?.code);
            getNewEmptySlot(AI_SERVICE_CODES.CAR_PARK_MONITORING, item?.code);
        } else {
            setShowModalPositionCarPark(false);
        }

        setCountPositionCarPart((prev) => ({ ...prev, show: check }));
    }, [item, refreshCamera]);

    return {
        countPositionCarPart,
        showModalPositionCarPark,
        listPosition,
    };
};

